import React, { useState, useEffect, useCallback } from 'react';
import { Button, Space, Form, Input, Popconfirm, DatePicker, message, Table } from 'antd';
import DraggableModal from '../../components/DraggableModal';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api';

const DEFAULT_DATE_RANGE = [dayjs().startOf('month'), dayjs().endOf('month')];

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [deleteFilterForm] = Form.useForm();
  const [deleteFilterModalOpen, setDeleteFilterModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [dateRange, setDateRange] = useState(DEFAULT_DATE_RANGE);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchData = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (keyword) params.keyword = keyword;
      if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
      const res = await api.get('/messages/admin', { params });
      setAnnouncements(Array.isArray(res?.items) ? res.items : []);
      setPagination({ current: page, pageSize, total: res?.total || 0 });
      setSelectedRowKeys([]);
    } catch (err) {
      message.error(err.message || '获取公告失败');
    }
    setLoading(false);
  }, [keyword, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePublish = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await api.post('/messages', values);
      message.success('公告发布成功');
      form.resetFields();
      setModalOpen(false);
      fetchData(pagination.current, pagination.pageSize);
    } catch { /* validation or api error */ }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/messages/${id}`);
      message.success('删除成功');
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选要删除的公告');
      return;
    }
    try {
      const res = await api.post('/messages/delete-batch', { scope: 'ids', ids: selectedRowKeys });
      message.success(`批量删除成功（${res?.deleted_count || 0} 条）`);
      fetchData(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteByFilter = async () => {
    try {
      const values = await deleteFilterForm.validateFields();
      if (!values.keyword) {
        message.warning('请至少填写关键词，禁止空白删除');
        return;
      }
      const res = await api.post('/messages/delete-batch', {
        scope: 'filtered',
        keyword: values.keyword,
        start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
        end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
      });
      message.success(`按筛选条件删除成功（${res?.deleted_count || 0} 条）`);
      setDeleteFilterModalOpen(false);
      fetchData(1, pagination.pageSize);
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const handleReset = () => {
    setKeyword('');
    setDateRange(DEFAULT_DATE_RANGE);
  };

  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title' },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      render: (v) => (
        <div style={{
          maxWidth: 560,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        >
          {v}
        </div>
      ),
    },
    { title: '发布时间', dataIndex: 'create_time', key: 'create_time' },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm title="确认删除该公告？" onConfirm={() => handleDelete(record.id)}>
          <Button type="link" danger style={{ padding: 0 }}>
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>公告管理</h1>
        <Space>
          <Input
            placeholder="搜索公告标题/内容"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onPressEnter={() => fetchData(1, pagination.pageSize)}
            allowClear
            style={{ width: 260 }}
          />
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(values) => setDateRange(values || DEFAULT_DATE_RANGE)}
            allowClear={false}
          />
          <Button onClick={() => fetchData(1, pagination.pageSize)}>搜索</Button>
          <Button onClick={handleReset}>重置</Button>
          <Popconfirm title={`仅删除已勾选项：确认删除 ${selectedRowKeys.length} 条公告？`} onConfirm={handleDeleteSelected} disabled={!selectedRowKeys.length}>
            <Button danger disabled={!selectedRowKeys.length}>删除已勾选</Button>
          </Popconfirm>
          <Button danger onClick={() => { deleteFilterForm.resetFields(); setDeleteFilterModalOpen(true); }}>按筛选条件删除</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }}>
            发布公告
          </Button>
        </Space>
      </div>

      <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
        共 <span style={{ fontSize: 20, fontWeight: 600, color: '#333' }}>{pagination.total}</span> 条公告
      </div>

      <Table
        rowKey="id"
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        columns={columns}
        dataSource={announcements}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => fetchData(page, pageSize),
          showTotal: t => `共 ${t} 条`,
        }}
      />

      {/* Publish Modal */}
      <DraggableModal
        title="按筛选条件删除公告（需重新填写）"
        open={deleteFilterModalOpen}
        onOk={handleDeleteByFilter}
        onCancel={() => setDeleteFilterModalOpen(false)}
        okText="确认删除"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 12, color: '#cf1322' }}>
          为避免误删，必须重新填写筛选条件；禁止空白条件删除。
        </div>
        <Form form={deleteFilterForm} layout="vertical">
          <Form.Item name="keyword" label="关键词（标题/内容）">
            <Input allowClear />
          </Form.Item>
        </Form>
      </DraggableModal>

      <DraggableModal
        title="发布公告"
        open={modalOpen}
        onOk={handlePublish}
        confirmLoading={loading}
        onCancel={() => setModalOpen(false)}
        okText="发布"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="公告标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="请输入公告标题" />
          </Form.Item>
          <Form.Item name="content" label="公告内容" rules={[{ required: true, message: '请输入内容' }]}>
            <Input.TextArea rows={4} placeholder="请输入公告内容" />
          </Form.Item>
        </Form>
      </DraggableModal>
    </div>
  );
}
